import { Button, Form, Input, Modal, ModalProps, Select, Spin, notification } from 'antd';
import { NAME_REGEX, PASSWORD_REGEX } from 'pages/Administration/constants';

import { IRegistrationAdmin } from 'types/administration.types';
import { LoadingOutlined } from '@ant-design/icons';
import { getFormErrorFields } from 'utils/errors';
import { roleOptions } from 'constants/roles';
import { useState } from 'react';

type AdministrationModalProps = {
  onHideModal: () => void;
  onCreate: (data: Omit<IRegistrationAdmin, 'confirm'>) => Promise<any>;
  onSuccess?: () => void;
} & ModalProps;

export const RegistrationAdminModal = ({
  onHideModal,
  onCreate,
  onSuccess,
  ...modalProps
}: AdministrationModalProps) => {
  const [form] = Form.useForm<IRegistrationAdmin>();
  const [loading, setLoading] = useState(false);

  const handleSubmit = () => {
    form.submit();
  };

  const handleFinish = async ({ confirm, ...data }: IRegistrationAdmin) => {
    try {
      setLoading(true);
      await onCreate(data);
      onSuccess && onSuccess();
      form.resetFields();
      onHideModal();
    } catch (e: any) {
      if (e && e.violations) {
        const errorFields = getFormErrorFields(e.violations, {});
        form.setFields(errorFields);
      } else {
        notification.error(e);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      centered
      onCancel={onHideModal}
      {...modalProps}
      footer={[
        <Button key="create" type="primary" onClick={handleSubmit} loading={loading}>
          Создать
        </Button>,
      ]}
    >
      <Form
        name="createAdmin"
        form={form}
        onFinish={handleFinish}
        autoComplete="off"
        layout="vertical"
        labelWrap
        initialValues={{}}
        preserve={false}
        scrollToFirstError={true}
      >
        <Spin spinning={loading} size="default" indicator={<LoadingOutlined spin />}>
          <Form.Item
            name="email"
            label="Логин (e-mail)"
            rules={[
              {
                required: true,
                message: 'Обязательное поле',
              },
              {
                type: 'email',
                message: 'Неверный формат почты',
              },
            ]}
          >
            <Input placeholder="Введите e-mail" />
          </Form.Item>
          <Form.Item
            name="surname"
            label="Фамилия"
            rules={[
              {
                required: true,
                message: 'Обязательное поле',
              },
              {
                min: 1,
                max: 32,
                pattern: NAME_REGEX,
                // eslint-disable-next-line no-template-curly-in-string
                message:
                  '${label} должна включать не менее ${min} и не более ${max} символов, содержать только русские или латинские буквы и символ -',
              },
            ]}
          >
            <Input placeholder="Введите фамилию" />
          </Form.Item>
          <Form.Item
            name="name"
            label="Имя"
            rules={[
              {
                required: true,
                message: 'Обязательное поле',
              },
              {
                min: 1,
                max: 32,
                pattern: NAME_REGEX,
                // eslint-disable-next-line no-template-curly-in-string
                message:
                  '${label} должно включать не менее ${min} и не более ${max} символов, содержать только русские или латинские буквы и символ -',
              },
            ]}
          >
            <Input placeholder="Введите имя" />
          </Form.Item>
          <Form.Item
            name="patronymic"
            label="Отчество"
            rules={[
              {
                required: true,
                message: 'Обязательное поле',
              },
              {
                min: 1,
                max: 32,
                pattern: NAME_REGEX,
                // eslint-disable-next-line no-template-curly-in-string
                message:
                  '${label} должно включать не менее ${min} и не более ${max} символов, содержать только русские или латинские буквы и символ -',
              },
            ]}
          >
            <Input placeholder="Введите отчество" />
          </Form.Item>
          <Form.Item
            name="role"
            label="Роль"
            rules={[
              {
                required: true,
                message: 'Обязательное поле',
              },
            ]}
          >
            <Select placeholder="Выберите роль" options={roleOptions} />
          </Form.Item>
          <Form.Item
            name="password"
            label="Пароль"
            rules={[
              {
                required: true,
                message: 'Обязательное поле',
              },
              {
                min: 6,
                max: 30,
                pattern: PASSWORD_REGEX,
                // eslint-disable-next-line no-template-curly-in-string
                message:
                  'Пароль должен включать не менее ${min} и не более ${max} символов, содержать только латинские буквы, цифры и специальные символы',
              },
            ]}
            hasFeedback
          >
            <Input.Password placeholder="Введите пароль" />
          </Form.Item>
          <Form.Item
            name="confirm"
            label="Подтверждение пароля"
            dependencies={['password']}
            hasFeedback
            rules={[
              {
                required: true,
                message: 'Подтвердите свой пароль',
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!!value && getFieldValue('password') !== value) {
                    return Promise.reject(new Error('Пароли не совпадают'));
                  }
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <Input.Password placeholder="Введите пароль повторно" />
          </Form.Item>
        </Spin>
      </Form>
    </Modal>
  );
};
